import CookieBanner from "@/components/marketing/CookieBanner";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { publicRoutes } from "@/i18n/routes";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import AdminRoute from "@/components/auth/AdminRoute";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import Index from "./pages/Index";
import ScrollToTop from "@/components/ScrollToTop";
import HreflangTags from "./components/HreflangTags";
import AttributionTracker from "@/components/AttributionTracker";
import { lazy, Suspense } from "react";

// Lazy load all pages except the landing page (Index) for fast initial load
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NewShipment = lazy(() => import("./pages/NewShipment"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const PublicInvoice = lazy(() => import("./pages/PublicInvoice"));
const PublicCollectiveInvoice = lazy(() => import("./pages/PublicCollectiveInvoice"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Settings = lazy(() => import("./pages/Settings"));
const Help = lazy(() => import("./pages/Help"));
const AddressBook = lazy(() => import("./pages/AddressBook"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderApproval = lazy(() => import("./pages/OrderApproval"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Addons = lazy(() => import("./pages/Addons"));
const DashboardShop = lazy(() => import("./pages/DashboardShop"));
const Referral = lazy(() => import("./pages/Referral"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const SubscriptionApproval = lazy(() => import("./pages/SubscriptionApproval"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Shop = lazy(() => import("./pages/Shop"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const EmbeddedCheckout = lazy(() => import("./pages/EmbeddedCheckout"));
const PlanningConfig = lazy(() => import("./pages/PlanningConfig"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const MedischFarmaceutisch = lazy(() => import("./pages/solutions/InternationaleZendingen"));
const IndustrieelReinigen = lazy(() => import("./pages/solutions/ReinigenMetDroogijs"));
const TransportLogistiek = lazy(() => import("./pages/solutions/GrootschaligContainers"));
const Voedselindustrie = lazy(() => import("./pages/solutions/EcommerceOplossingen"));
const HorecaCatering = lazy(() => import("./pages/solutions/HorecaAbonnement"));
const Evenementen = lazy(() => import("./pages/solutions/Evenementen"));
const TrackTrace = lazy(() => import("./pages/TrackTrace"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const QuoteRequest = lazy(() => import("./pages/QuoteRequest"));
const Kennisbank = lazy(() => import("./pages/Kennisbank"));
const WatIsDroogijs = lazy(() => import("./pages/kennisbank/WatIsDroogijs"));
const VeiligWerkenMetDroogijs = lazy(() => import("./pages/kennisbank/VeiligWerkenMetDroogijs"));
const DroogijsOpslaan = lazy(() => import("./pages/kennisbank/DroogijsOpslaan"));
const DroogijsVoedselvervoer = lazy(() => import("./pages/kennisbank/DroogijsVoedselvervoer"));
const LeveringLogistiek = lazy(() => import("./pages/kennisbank/LeveringLogistiek"));
const IndustrieleReiniging = lazy(() => import("./pages/kennisbank/IndustrieleReiniging"));
const MedischeVerzendingen = lazy(() => import("./pages/kennisbank/MedischeVerzendingen"));
const DroogijsVsGelPacks = lazy(() => import("./pages/kennisbank/DroogijsVsGelPacks"));
const EvenementenHoreca = lazy(() => import("./pages/kennisbank/EvenementenHoreca"));
const DuurzaamheidDroogijs = lazy(() => import("./pages/kennisbank/DuurzaamheidDroogijs"));
const HoeveelDroogijsNodig = lazy(() => import("./pages/kennisbank/HoeveelDroogijsNodig"));
const VerpakkenVerzenden = lazy(() => import("./pages/kennisbank/VerpakkenVerzenden"));
const DroogijsBewarenZomer = lazy(() => import("./pages/kennisbank/DroogijsBewarenZomer"));
const KostenDroogijsPerKg = lazy(() => import("./pages/kennisbank/KostenDroogijsPerKg"));
const DroogijsLaboratorium = lazy(() => import("./pages/kennisbank/DroogijsLaboratorium"));
const DroogijsBloemisterij = lazy(() => import("./pages/kennisbank/DroogijsBloemisterij"));
const DroogijsFarmaceutisch = lazy(() => import("./pages/kennisbank/DroogijsFarmaceutisch"));
const DroogijsWijnkoeling = lazy(() => import("./pages/kennisbank/DroogijsWijnkoeling"));
const DroogijsRookeffecten = lazy(() => import("./pages/kennisbank/DroogijsRookeffecten"));
const DroogijsIjssalon = lazy(() => import("./pages/kennisbank/DroogijsIjssalon"));
const DroogijsLuchtvaart = lazy(() => import("./pages/kennisbank/DroogijsLuchtvaart"));
const DroogijsHalloween = lazy(() => import("./pages/kennisbank/DroogijsHalloween"));
const DroogijsFeestdagen = lazy(() => import("./pages/kennisbank/DroogijsFeestdagen"));
const DroogijsTemperatuurregistratie = lazy(() => import("./pages/kennisbank/DroogijsTemperatuurregistratie"));
const DroogijsVisverwerking = lazy(() => import("./pages/kennisbank/DroogijsVisverwerking"));
const DroogijsAutomotive = lazy(() => import("./pages/kennisbank/DroogijsAutomotive"));
const DroogijsCatering = lazy(() => import("./pages/kennisbank/DroogijsCatering"));
const DroogijsPelletsVsBlokken = lazy(() => import("./pages/kennisbank/DroogijsPelletsVsBlokken"));
const DroogijsEcommerce = lazy(() => import("./pages/kennisbank/DroogijsEcommerce"));
const DroogijsOpslagBedrijven = lazy(() => import("./pages/kennisbank/DroogijsOpslagBedrijven"));
const DroogijsGenderReveal = lazy(() => import("./pages/kennisbank/DroogijsGenderReveal"));
const DynamicArticle = lazy(() => import("./pages/kennisbank/DynamicArticle"));
const CityLanding = lazy(() => import("./pages/CityLanding"));
const Links = lazy(() => import("./pages/Links"));

const queryClient = new QueryClient();

/** Minimal loading fallback */
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

/** Wrap lazy element */
const S = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

/** Redirect /en/kennisbank/:slug to canonical /en/knowledge-base/:slug (translates slug if known). */
const EnKennisbankRedirect = () => {
  const { slug } = useParams<{ slug: string }>();
  const match = publicRoutes.find(
    (r) => r.nl === `/kennisbank/${slug}` && r.en !== r.nl
  );
  const enSlug = match ? match.en.replace("/knowledge-base/", "") : slug;
  return <Navigate to={`/en/knowledge-base/${enSlug}`} replace />;
};

/** Wrapper that applies theme A/B test to public pages */
const PublicThemeABTest = lazy(() => import("@/components/PublicThemeABTest"));

/** Public page routes, rendered for both NL (no prefix) and EN (/en prefix) */
const publicPages = [
  { nl: "/", en: "/", element: <Index /> },
  { nl: "/bestellen", en: "/order", element: <S><Shop /></S> },
  { nl: "/bestellen/:productType", en: "/order/:productType", element: <S><Shop /></S> },
  { nl: "/bestelling-succes", en: "/order-success", element: <S><PaymentSuccess /></S> },
  { nl: "/betaling-succes", en: "/payment-success", element: <S><PaymentSuccess /></S> },
  { nl: "/betalen", en: "/checkout", element: <S><EmbeddedCheckout /></S> },
  { nl: "/planning-configuratie", en: "/planning-config", element: <S><PlanningConfig /></S> },
  { nl: "/over-ons", en: "/about", element: <S><About /></S> },
  { nl: "/contact", en: "/contact", element: <S><Contact /></S> },
  { nl: "/faq", en: "/faq", element: <S><FAQ /></S> },
  { nl: "/oplossingen/horeca-catering", en: "/solutions/hospitality-catering", element: <S><HorecaCatering /></S> },
  { nl: "/oplossingen/medisch-farmaceutisch", en: "/solutions/medical-pharmaceutical", element: <S><MedischFarmaceutisch /></S> },
  { nl: "/oplossingen/transport-logistiek", en: "/solutions/transport-logistics", element: <S><TransportLogistiek /></S> },
  { nl: "/oplossingen/voedselindustrie", en: "/solutions/food-industry", element: <S><Voedselindustrie /></S> },
  { nl: "/oplossingen/industrieel-reinigen", en: "/solutions/industrial-cleaning", element: <S><IndustrieelReinigen /></S> },
  { nl: "/oplossingen/evenementen", en: "/solutions/events", element: <S><Evenementen /></S> },
  { nl: "/track", en: "/track", element: <S><TrackTrace /></S> },
  { nl: "/algemene-voorwaarden", en: "/terms", element: <S><Terms /></S> },
  { nl: "/privacy", en: "/privacy", element: <S><Privacy /></S> },
  { nl: "/offerte", en: "/quote", element: <S><QuoteRequest /></S> },
  { nl: "/abonnement/bevestigen", en: "/subscription/confirm", element: <S><SubscriptionApproval /></S> },
  { nl: "/kennisbank", en: "/knowledge-base", element: <S><Kennisbank /></S> },
  { nl: "/kennisbank/wat-is-droogijs", en: "/knowledge-base/what-is-dry-ice", element: <S><WatIsDroogijs /></S> },
  { nl: "/kennisbank/veilig-werken-met-droogijs", en: "/knowledge-base/working-safely-with-dry-ice", element: <S><VeiligWerkenMetDroogijs /></S> },
  { nl: "/kennisbank/droogijs-opslaan", en: "/knowledge-base/storing-dry-ice", element: <S><DroogijsOpslaan /></S> },
  { nl: "/kennisbank/droogijs-voor-voedselvervoer", en: "/knowledge-base/dry-ice-for-food-transport", element: <S><DroogijsVoedselvervoer /></S> },
  { nl: "/kennisbank/levering-en-logistiek", en: "/knowledge-base/delivery-and-logistics", element: <S><LeveringLogistiek /></S> },
  { nl: "/kennisbank/industriele-reiniging", en: "/knowledge-base/industrial-cleaning", element: <S><IndustrieleReiniging /></S> },
  { nl: "/kennisbank/droogijs-medische-verzendingen", en: "/knowledge-base/dry-ice-medical-shipping", element: <S><MedischeVerzendingen /></S> },
  { nl: "/kennisbank/droogijs-vs-gel-packs", en: "/knowledge-base/dry-ice-vs-gel-packs", element: <S><DroogijsVsGelPacks /></S> },
  { nl: "/kennisbank/droogijs-evenementen-horeca", en: "/knowledge-base/dry-ice-events-hospitality", element: <S><EvenementenHoreca /></S> },
  { nl: "/kennisbank/duurzaamheid-droogijs", en: "/knowledge-base/dry-ice-sustainability", element: <S><DuurzaamheidDroogijs /></S> },
  { nl: "/kennisbank/hoeveel-droogijs-nodig", en: "/knowledge-base/how-much-dry-ice", element: <S><HoeveelDroogijsNodig /></S> },
  { nl: "/kennisbank/droogijs-verpakken-verzenden", en: "/knowledge-base/packaging-shipping-dry-ice", element: <S><VerpakkenVerzenden /></S> },
  { nl: "/kennisbank/droogijs-bewaren-zomer", en: "/knowledge-base/storing-dry-ice-summer", element: <S><DroogijsBewarenZomer /></S> },
  { nl: "/kennisbank/kosten-droogijs-per-kg", en: "/knowledge-base/dry-ice-cost-per-kg", element: <S><KostenDroogijsPerKg /></S> },
  { nl: "/kennisbank/droogijs-laboratorium", en: "/knowledge-base/dry-ice-laboratory", element: <S><DroogijsLaboratorium /></S> },
  { nl: "/kennisbank/droogijs-bloemisterij", en: "/knowledge-base/dry-ice-florists", element: <S><DroogijsBloemisterij /></S> },
  { nl: "/kennisbank/droogijs-farmaceutische-industrie", en: "/knowledge-base/dry-ice-pharmaceutical", element: <S><DroogijsFarmaceutisch /></S> },
  { nl: "/kennisbank/droogijs-wijnkoeling", en: "/knowledge-base/dry-ice-wine-cooling", element: <S><DroogijsWijnkoeling /></S> },
  { nl: "/kennisbank/droogijs-rookeffecten-theater", en: "/knowledge-base/dry-ice-smoke-effects", element: <S><DroogijsRookeffecten /></S> },
  { nl: "/kennisbank/droogijs-ijssalon-bevroren-desserts", en: "/knowledge-base/dry-ice-ice-cream", element: <S><DroogijsIjssalon /></S> },
  { nl: "/kennisbank/droogijs-luchtvaart-luchtvracht", en: "/knowledge-base/dry-ice-aviation", element: <S><DroogijsLuchtvaart /></S> },
  { nl: "/kennisbank/droogijs-halloween-feesten", en: "/knowledge-base/dry-ice-halloween", element: <S><DroogijsHalloween /></S> },
  { nl: "/kennisbank/droogijs-feestdagen-kerst", en: "/knowledge-base/dry-ice-holidays", element: <S><DroogijsFeestdagen /></S> },
  { nl: "/kennisbank/droogijs-temperatuurregistratie", en: "/knowledge-base/dry-ice-temperature-monitoring", element: <S><DroogijsTemperatuurregistratie /></S> },
  { nl: "/kennisbank/droogijs-visverwerking", en: "/knowledge-base/dry-ice-fish-processing", element: <S><DroogijsVisverwerking /></S> },
  { nl: "/kennisbank/droogijs-automotive-industrie", en: "/knowledge-base/dry-ice-automotive", element: <S><DroogijsAutomotive /></S> },
  { nl: "/kennisbank/droogijs-catering-bedrijven", en: "/knowledge-base/dry-ice-catering", element: <S><DroogijsCatering /></S> },
  { nl: "/kennisbank/droogijs-pellets-vs-blokken", en: "/knowledge-base/dry-ice-pellets-vs-blocks", element: <S><DroogijsPelletsVsBlokken /></S> },
  { nl: "/kennisbank/droogijs-ecommerce-verzendingen", en: "/knowledge-base/dry-ice-ecommerce", element: <S><DroogijsEcommerce /></S> },
  { nl: "/kennisbank/droogijs-opslag-tips-bedrijven", en: "/knowledge-base/dry-ice-business-storage", element: <S><DroogijsOpslagBedrijven /></S> },
  { nl: "/kennisbank/droogijs-gender-reveal", en: "/knowledge-base/dry-ice-gender-reveal", element: <S><DroogijsGenderReveal /></S> },
  { nl: "/kennisbank/:slug", en: "/knowledge-base/:slug", element: <S><DynamicArticle /></S> },
  { nl: "/droogijs-kopen-amsterdam", en: "/buy-dry-ice-amsterdam", element: <S><CityLanding citySlug="amsterdam" /></S> },
  { nl: "/droogijs-kopen-rotterdam", en: "/buy-dry-ice-rotterdam", element: <S><CityLanding citySlug="rotterdam" /></S> },
  { nl: "/droogijs-kopen-den-haag", en: "/buy-dry-ice-the-hague", element: <S><CityLanding citySlug="den-haag" /></S> },
  { nl: "/droogijs-kopen-utrecht", en: "/buy-dry-ice-utrecht", element: <S><CityLanding citySlug="utrecht" /></S> },
  { nl: "/droogijs-kopen-eindhoven", en: "/buy-dry-ice-eindhoven", element: <S><CityLanding citySlug="eindhoven" /></S> },
  { nl: "/droogijs-kopen-groningen", en: "/buy-dry-ice-groningen", element: <S><CityLanding citySlug="groningen" /></S> },
  { nl: "/droogijs-kopen-tilburg", en: "/buy-dry-ice-tilburg", element: <S><CityLanding citySlug="tilburg" /></S> },
  { nl: "/droogijs-kopen-almere", en: "/buy-dry-ice-almere", element: <S><CityLanding citySlug="almere" /></S> },
  { nl: "/droogijs-kopen-breda", en: "/buy-dry-ice-breda", element: <S><CityLanding citySlug="breda" /></S> },
  { nl: "/droogijs-kopen-arnhem", en: "/buy-dry-ice-arnhem", element: <S><CityLanding citySlug="arnhem" /></S> },
  { nl: "/login", en: "/login", element: <S><Login /></S> },
  { nl: "/signup", en: "/signup", element: <S><Signup /></S> },
  { nl: "/forgot-password", en: "/forgot-password", element: <S><ForgotPassword /></S> },
  { nl: "/reset-password", en: "/reset-password", element: <S><ResetPassword /></S> },
  { nl: "/links", en: "/links", element: <S><Links /></S> },
];

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <HreflangTags />
        <AttributionTracker />
        <CookieBanner />
        <Suspense fallback={null}><PublicThemeABTest /></Suspense>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* NL routes (default, no prefix) */}
            {publicPages.map((page) => (
              <Route key={`nl-${page.nl}`} path={page.nl} element={page.element} />
            ))}

            {/* EN routes (/en prefix) */}
            {publicPages.map((page) => (
              <Route key={`en-${page.en}`} path={`/en${page.en}`} element={page.element} />
            ))}

            {/* SEO: redirect EN-only slugs accessed without /en prefix to canonical /en/... */}
            {[
              "/order", "/order/:productType", "/order-success", "/payment-success",
              "/checkout", "/planning-config", "/about", "/faq",
              "/solutions/hospitality-catering", "/solutions/medical-pharmaceutical",
              "/solutions/transport-logistics", "/solutions/food-industry",
              "/solutions/industrial-cleaning", "/solutions/events",
              "/terms", "/quote", "/knowledge-base",
              "/subscription/confirm",
              "/buy-dry-ice-amsterdam", "/buy-dry-ice-rotterdam", "/buy-dry-ice-the-hague",
              "/buy-dry-ice-utrecht", "/buy-dry-ice-eindhoven", "/buy-dry-ice-groningen",
              "/buy-dry-ice-tilburg", "/buy-dry-ice-almere", "/buy-dry-ice-breda",
              "/buy-dry-ice-arnhem",
            ].map((p) => (
              <Route key={`enredir-${p}`} path={p} element={<Navigate to={`/en${p}`} replace />} />
            ))}

            {/* SEO: /en/kennisbank/* (mixed prefix) -> redirect to canonical /en/knowledge-base/* */}
            <Route path="/en/kennisbank/:slug" element={<EnKennisbankRedirect />} />

            <Route path="/factuur/:orderId" element={<S><PublicInvoice /></S>} />
            <Route path="/verzamelfactuur/:invoiceId" element={<S><PublicCollectiveInvoice /></S>} />
            {/* Dashboard routes (no language prefix, behind auth) */}
            <Route element={<DashboardLayout />}>
              <Route path="/dashboard" element={<S><Dashboard /></S>} />
              <Route path="/dashboard/new" element={<S><NewShipment /></S>} />
              <Route path="/dashboard/order-success" element={<S><OrderSuccess /></S>} />
              <Route path="/dashboard/orders" element={<S><Orders /></S>} />
              <Route path="/dashboard/goedkeuren" element={<S><OrderApproval /></S>} />
              <Route path="/dashboard/invoices" element={<S><Invoices /></S>} />
              <Route path="/dashboard/addresses" element={<S><AddressBook /></S>} />
              <Route path="/dashboard/inbox" element={<S><Inbox /></S>} />
              <Route path="/dashboard/shop" element={<S><DashboardShop /></S>} />
              <Route path="/dashboard/addons" element={<S><Addons /></S>} />
              <Route path="/dashboard/subscriptions" element={<S><Subscriptions /></S>} />
              <Route path="/dashboard/settings" element={<S><Settings /></S>} />
              <Route path="/dashboard/referral" element={<S><Referral /></S>} />
              <Route path="/dashboard/help" element={<S><Help /></S>} />
            </Route>

            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <S><AdminDashboard /></S>
                </AdminRoute>
              }
            />
            <Route path="*" element={<S><NotFound /></S>} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;
